// ============================================================================
// MÓDULO 7 - REVIEW FINAL
// ============================================================================
// 5 Sets × 12 preguntas = 60 preguntas totales
// Progresión: Foundations → Daily Life → Skills → Advanced → Complete Mastery
// Repasa TODAS las 12 unidades del módulo
// ============================================================================

export const M7_MODULE_REVIEW = {
  id: "m7-final-review",
  title: "⭐ Módulo 7 - Final Review",
  
  sets: [
    // ========================================================================
    // SET 1: FOUNDATIONS (Units 1, 2, 3)
    // ========================================================================
    {
      id: "m7-review-s1",
      title: "⭐ Review Set 1: Time, Hobbies & Perfect Tenses",
      context: `**Units 1-3 Review**
Routines, Hobbies, Present Perfect vs Past Simple`,
      category: "review",
      isReview: true,
      
      questions: [
        {
          type: "mc",
          q: "Which frequency adverb means 100%?",
          options: [
            "Always",
            "Sometimes",
            "Never"
          ],
          a: "Always",
          successtip: "✓ ALWAYS = 100%!\nUnit 1: Frequency scale! Never → Always! ⏰"
        },
        
        {
          type: "input",
          q: "Preposition for exact time: 'I wake up ___ 7 AM.'",
          a: "at",
          accept: { ignoreCase: true },
          successtip: "✓ AT + TIME!\nAt + exact time! On + day! In + month/year! Unit 1! ⏰"
        },
        
        {
          type: "mc",
          q: "Activity pattern: 'go ___' (andar en bici)",
          options: [
            "cycling (go + -ing)",
            "cycle",
            "to cycle"
          ],
          a: "cycling (go + -ing)",
          successtip: "✓ GO + -ING!\nGo cycling, go swimming, go shopping! Unit 2: Hobby pattern! 🚴"
        },
        
        {
          type: "order",
          q: "Complete: 'play ___' (fútbol - NOT 'football game')",
          a: "football",
          successtip: "✓ PLAY FOOTBALL!\nPlay + sport without article! Unit 2: Sports! ⚽"
        },
        
        {
          type: "mc",
          q: "'I ___ visited Paris' (life experience, no specific time)",
          options: [
            "have (present perfect)",
            "did",
            "was"
          ],
          a: "have (present perfect)",
          successtip: "✓ PRESENT PERFECT!\nHave + past participle = experience! Unit 3: Critical! ✅"
        },
        
        {
          type: "input",
          q: "'I visited Paris ___ ___.' (specific time requires past simple)",
          a: "last year",
          accept: { ignoreCase: true, alts: ["last week", "last month", "in 2020", "yesterday", "two years ago"] },
          successtip: "✓ PAST SIMPLE!\nSpecific time = past simple! Last year, yesterday, in 2020! Unit 3! 📅"
        },
        
        {
          type: "mc",
          q: "Which requires past simple? (time expression)",
          options: [
            "Yesterday / Last week / In 2020 (specific time)",
            "Ever / Never / Already (no specific time)",
            "Since / For (duration)"
          ],
          a: "Yesterday / Last week / In 2020 (specific time)",
          successtip: "✓ SPECIFIC TIME!\nYesterday, last week, in 2020 = specific! Must use past simple! Unit 3! ⏰"
        },
        
        {
          type: "order",
          q: "Present perfect structure: ___ + past participle",
          a: "have/has",
          successtip: "✓ STRUCTURE!\nHave/Has + past participle! I have done, She has gone! Unit 3! ✅"
        },
        
        {
          type: "mc",
          q: "'I ___ there for 3 years.' (duration from past until now)",
          options: [
            "have lived (present perfect)",
            "lived",
            "live"
          ],
          a: "have lived (present perfect)",
          successtip: "✓ FOR + DURATION!\n'For' with present perfect = duration until now! Unit 3! ⏳"
        },
        
        {
          type: "input",
          q: "'___ you ever been to London?' (experience question)",
          a: "Have",
          accept: { ignoreCase: true },
          successtip: "✓ HAVE EVER!\n'Have you ever...?' = life experience question! Unit 3: Pattern! 🌍"
        },
        
        {
          type: "mc",
          q: "Which hobby uses 'do'? (not 'play' or 'go')",
          options: [
            "do yoga / do karate",
            "do cycling",
            "do football"
          ],
          a: "do yoga / do karate",
          successtip: "✓ DO + ACTIVITY!\nDo yoga, do karate! Individual activities! Unit 2: Patterns! 🧘"
        },
        
        {
          type: "mc",
          q: "Units 1-3 cover:",
          options: [
            "Routines & time + Hobbies + Present perfect vs Past simple",
            "Only grammar",
            "Only vocabulary"
          ],
          a: "Routines & time + Hobbies + Present perfect vs Past simple",
          successtip: "✅ UNITS 1-3 REVIEWED!\n\n✓ UNIT 1: Routines & Time\n• Frequency adverbs (always, never) ✓\n• Time prepositions (at, on, in) ✓\n• Daily routine vocabulary ✓\n\n✓ UNIT 2: Hobbies & Free Time\n• Go + -ing (cycling, swimming) ✓\n• Play + sport (football, tennis) ✓\n• Do + activity (yoga, karate) ✓\n\n✓ UNIT 3: Present Perfect vs Past\n• Present perfect = experience ✓\n• Past simple = specific time ✓\n• Have/Has + past participle ✓\n• For/Since with duration ✓\n\nFoundations solid! Next: Daily Life! 💪"
        }
      ]
    },

    // ========================================================================
    // SET 2: DAILY LIFE (Units 4, 5, 6)
    // ========================================================================
    {
      id: "m7-review-s2",
      title: "⭐ Review Set 2: Food, Sports & Money",
      context: `**Units 4-6 Review**
Food & Cooking, Sports & Fitness, Shopping & Money`,
      category: "review",
      isReview: true,
      
      questions: [
        {
          type: "mc",
          q: "Which is a vegetable?",
          options: [
            "Carrot / Onion / Pepper",
            "Apple / Banana",
            "Chicken / Beef"
          ],
          a: "Carrot / Onion / Pepper",
          successtip: "✓ VEGETABLES!\nCarrot, onion, pepper, tomato! Unit 4: Food categories! 🥕"
        },
        
        {
          type: "input",
          q: "Cooking method with oil in a pan: '___' (freír)",
          a: "fry",
          accept: { ignoreCase: true },
          successtip: "✓ FRY!\nFry = freír in oil/pan! Unit 4: Cooking methods! 🍳"
        },
        
        {
          type: "mc",
          q: "Verb for sports with ball (football, tennis):",
          options: [
            "play",
            "do",
            "go"
          ],
          a: "play",
          successtip: "✓ PLAY!\nPlay + ball sports! Play football, play tennis! Unit 5: Patterns! ⚽"
        },
        
        {
          type: "order",
          q: "Verb for individual exercise: '___ yoga' (not play)",
          a: "do",
          successtip: "✓ DO YOGA!\nDo + individual exercise! Do yoga, do push-ups! Unit 5! 🧘"
        },
        
        {
          type: "mc",
          q: "Activity verb: '___ jogging' (correr como actividad)",
          options: [
            "go",
            "play",
            "do"
          ],
          a: "go",
          successtip: "✓ GO JOGGING!\nGo + -ing activities! Go jogging, go running! Unit 5! 🏃"
        },
        
        {
          type: "input",
          q: "Place to exercise with equipment: the ___",
          a: "gym",
          accept: { ignoreCase: true },
          successtip: "✓ GYM!\nGym = gimnasio! Has equipment, weights, machines! Unit 5! 💪"
        },
        
        {
          type: "mc",
          q: "'This shirt ___ £25.' (costs)",
          options: [
            "costs",
            "pays",
            "spends"
          ],
          a: "costs",
          successtip: "✓ COST!\nThings COST money! People SPEND/PAY! Unit 6: Critical! 💷"
        },
        
        {
          type: "order",
          q: "Person action: 'I ___ £25 for the shirt.' (give money)",
          a: "paid",
          successtip: "✓ PAY!\nPeople PAY for things! Unit 6: Money verbs! 💳"
        },
        
        {
          type: "mc",
          q: "To use money: 'I ___ £50 on groceries.'",
          options: [
            "spent",
            "cost",
            "earned"
          ],
          a: "spent",
          successtip: "✓ SPEND!\nSpend + money + on! People spend money! Unit 6! 💰"
        },
        
        {
          type: "input",
          q: "Payment method (not cash): credit ___",
          a: "card",
          accept: { ignoreCase: true },
          successtip: "✓ CREDIT CARD!\nPay by credit card! Unit 6: Payment methods! 💳"
        },
        
        {
          type: "mc",
          q: "US: 'check' = UK: '___' (restaurant bill)",
          options: [
            "bill",
            "cost",
            "price"
          ],
          a: "bill",
          successtip: "✓ BILL!\nUS: check. UK: bill. Both = restaurant bill! Unit 6! 🧾"
        },
        
        {
          type: "mc",
          q: "Units 4-6 cover:",
          options: [
            "Food & cooking + Sports & fitness + Shopping & money",
            "Only food",
            "Only sports"
          ],
          a: "Food & cooking + Sports & fitness + Shopping & money",
          successtip: "✅ UNITS 4-6 REVIEWED!\n\n✓ UNIT 4: Food & Cooking\n• Food categories (vegetables, fruit, meat) ✓\n• Cooking methods (fry, bake, boil) ✓\n• Kitchen vocabulary ✓\n\n✓ UNIT 5: Sports & Fitness\n• Play + ball sports ✓\n• Do + individual exercise ✓\n• Go + -ing activities ✓\n• Gym vocabulary ✓\n\n✓ UNIT 6: Shopping & Money\n• Cost vs Spend vs Pay ✓\n• Payment methods ✓\n• Price/Bill/Receipt ✓\n• UK vs US differences ✓\n\nDaily life vocabulary solid! 💪"
        }
      ]
    },

    // ========================================================================
    // SET 3: INTERMEDIATE SKILLS (Units 7, 8, 9)
    // ========================================================================
    {
      id: "m7-review-s3",
      title: "⭐ Review Set 3: Phrasal Verbs, Comparing & Preferences",
      context: `**Units 7-9 Review**
Cities & Phrasal Verbs, Comparing Places, Likes & Dislikes`,
      category: "review",
      isReview: true,
      
      questions: [
        {
          type: "mc",
          q: "'Grow up' means:",
          options: [
            "Spend childhood (phrasal verb)",
            "Become taller",
            "Plant something"
          ],
          a: "Spend childhood (phrasal verb)",
          successtip: "✓ GROW UP!\nGrow up = pasar la infancia! Unit 7: Phrasal verbs! 👶➡️👨"
        },
        
        {
          type: "input",
          q: "'Find ___' means discover. (phrasal verb)",
          a: "out",
          accept: { ignoreCase: true },
          successtip: "✓ FIND OUT!\nFind out = descubrir/enterarse! Unit 7: Phrasal verbs! 🔍"
        },
        
        {
          type: "mc",
          q: "'Give up' means:",
          options: [
            "Stop/quit (abandon)",
            "Start something",
            "Continue"
          ],
          a: "Stop/quit (abandon)",
          successtip: "✓ GIVE UP!\nGive up = dejar/abandonar! Unit 7: Phrasal verbs! 🚫"
        },
        
        {
          type: "order",
          q: "One-syllable adjective comparative: 'big' → '___'",
          a: "bigger",
          successtip: "✓ BIGGER!\nShort adjective + -er! Big → bigger! Unit 8: Comparatives! 📏"
        },
        
        {
          type: "mc",
          q: "Long adjective comparative: 'expensive' → '___'",
          options: [
            "more expensive",
            "expensiver",
            "most expensive"
          ],
          a: "more expensive",
          successtip: "✓ MORE EXPENSIVE!\nLong adjective = more + adjective! Unit 8: Pattern! 💰"
        },
        
        {
          type: "input",
          q: "'Good' → '___ ' (irregular comparative)",
          a: "better",
          accept: { ignoreCase: true },
          successtip: "✓ BETTER!\nGood → Better → Best! Irregular! Unit 8: Critical! ⭐"
        },
        
        {
          type: "mc",
          q: "'Bad' → '___' (irregular comparative)",
          options: [
            "worse",
            "badder",
            "more bad"
          ],
          a: "worse",
          successtip: "✓ WORSE!\nBad → Worse → Worst! Irregular! Unit 8: Critical! 👎"
        },
        
        {
          type: "order",
          q: "Strong like: 'I ___ chocolate!' (not just 'like')",
          a: "love",
          successtip: "✓ LOVE!\nLove = strong like! Unit 9: Intensity scale! ❤️"
        },
        
        {
          type: "mc",
          q: "Strong dislike: '___ spiders!' (not just 'dislike')",
          options: [
            "I hate / I can't stand",
            "I like",
            "I love"
          ],
          a: "I hate / I can't stand",
          successtip: "✓ HATE!\nHate / Can't stand = strong dislike! Unit 9: Intensity! 😠"
        },
        
        {
          type: "input",
          q: "'I'm really ___ music.' (strong interest - preposition 'into')",
          a: "into",
          accept: { ignoreCase: true },
          successtip: "✓ BE INTO!\nBe into = strong interest! Unit 9: Collocation! 🎵"
        },
        
        {
          type: "mc",
          q: "'I'm keen ___' = enthusiastic about",
          options: [
            "on",
            "in",
            "at"
          ],
          a: "on",
          successtip: "✓ KEEN ON!\nKeen on = enthusiastic! Unit 9: Preference phrase! ⭐"
        },
        
        {
          type: "mc",
          q: "Units 7-9 cover:",
          options: [
            "Phrasal verbs + Comparing (big→bigger, more expensive) + Preferences (love, hate, be into)",
            "Only phrasal verbs",
            "Only comparing"
          ],
          a: "Phrasal verbs + Comparing (big→bigger, more expensive) + Preferences (love, hate, be into)",
          successtip: "✅ UNITS 7-9 REVIEWED!\n\n✓ UNIT 7: Cities & Phrasal Verbs\n• Grow up, find out, give up ✓\n• Look after, run into ✓\n• Separable vs inseparable ✓\n\n✓ UNIT 8: Comparing Places\n• Short: -er (bigger, smaller) ✓\n• Long: more + adj (more expensive) ✓\n• Irregular: better, worse ✓\n• Than structure ✓\n\n✓ UNIT 9: Likes & Dislikes\n• Love → Like → Don't mind → Dislike → Hate ✓\n• Be into, be fond of, keen on ✓\n• Prefer, would rather ✓\n\nIntermediate skills mastered! 💪"
        }
      ]
    },

    // ========================================================================
    // SET 4: ADVANCED TOPICS (Units 10, 11, 12)
    // ========================================================================
    {
      id: "m7-review-s4",
      title: "⭐ Review Set 4: Family, Education & Travel",
      context: `**Units 10-12 Review**
Family & Early Years, Education & Word Formation, Travel & Transportation`,
      category: "review",
      isReview: true,
      
      questions: [
        {
          type: "mc",
          q: "Your aunt/uncle's child is your:",
          options: [
            "Cousin",
            "Niece/Nephew",
            "Sibling"
          ],
          a: "Cousin",
          successtip: "✓ COUSIN!\nCousin = primo/a! Same generation! Unit 10: Family! 👥"
        },
        
        {
          type: "input",
          q: "Your brother's wife is your ___-in-law.",
          a: "sister",
          accept: { ignoreCase: true },
          successtip: "✓ SISTER-IN-LAW!\nSibling's spouse = in-law! Unit 10: Family terms! 💍"
        },
        
        {
          type: "mc",
          q: "Child age 1-3 years:",
          options: [
            "Toddler",
            "Baby",
            "Teenager"
          ],
          a: "Toddler",
          successtip: "✓ TODDLER!\nToddler = 1-3 years, learning to walk! Unit 10: Life stages! 👶"
        },
        
        {
          type: "order",
          q: "Prefix UN- means: '___' or opposite",
          a: "not",
          successtip: "✓ UN- = NOT!\nUnhappy, unlikely, untidy! Unit 11: Word formation! 🚫"
        },
        
        {
          type: "mc",
          q: "Prefix RE- means:",
          options: [
            "Again/back",
            "Not",
            "Full of"
          ],
          a: "Again/back",
          successtip: "✓ RE- = AGAIN!\nRewrite, retake, rebuild! Unit 11: Prefixes! 🔄"
        },
        
        {
          type: "input",
          q: "Suffix -FUL means: 'full ___'",
          a: "of",
          accept: { ignoreCase: true },
          successtip: "✓ -FUL = FULL OF!\nSuccessful, hopeful, wonderful! Unit 11: Suffixes! ✨"
        },
        
        {
          type: "mc",
          q: "Suffix -LESS means:",
          options: [
            "Without",
            "Full of",
            "Again"
          ],
          a: "Without",
          successtip: "✓ -LESS = WITHOUT!\nTasteless, pointless, hopeless! Unit 11: Suffixes! ❌"
        },
        
        {
          type: "order",
          q: "Collocation: '___ a car' (NOT 'conduct'!)",
          a: "drive",
          successtip: "✓ DRIVE!\nDrive a car! NOT conduct! Unit 12: Critical collocation! 🚗"
        },
        
        {
          type: "mc",
          q: "Collocation: '___ a bike' (NOT 'drive'!)",
          options: [
            "ride",
            "drive",
            "board"
          ],
          a: "ride",
          successtip: "✓ RIDE!\nRide a bike! NOT drive a bike! Unit 12: Collocations! 🚴"
        },
        
        {
          type: "input",
          q: "'Arrive ___ the airport' (preposition - NOT 'to'!)",
          a: "at",
          accept: { ignoreCase: true },
          successtip: "✓ ARRIVE AT!\nArrive AT (not TO!) airport/hotel! Unit 12: Critical! ✈️"
        },
        
        {
          type: "mc",
          q: "For planes, formal entry verb:",
          options: [
            "board",
            "get on",
            "enter"
          ],
          a: "board",
          successtip: "✓ BOARD!\nBoard a plane = abordar! Formal! Unit 12: Airport! ✈️"
        },
        
        {
          type: "mc",
          q: "Units 10-12 cover:",
          options: [
            "Family relationships + Word formation (UN-, RE-, -FUL, -LESS) + Travel collocations",
            "Only family",
            "Only travel"
          ],
          a: "Family relationships + Word formation (UN-, RE-, -FUL, -LESS) + Travel collocations",
          successtip: "✅ UNITS 10-12 REVIEWED!\n\n✓ UNIT 10: Family & Early Years\n• Extended family (cousin, aunt, uncle) ✓\n• In-laws (mother-in-law, etc.) ✓\n• Life stages (baby, toddler, child) ✓\n• Did for emphasis ✓\n\n✓ UNIT 11: Education & Word Formation\n• 17 academic subjects ✓\n• UN- (not), RE- (again) ✓\n• -FUL (full of), -LESS (without) ✓\n• Educational vocabulary ✓\n\n✓ UNIT 12: Travel & Transportation\n• 13 vehicles (land/air/water) ✓\n• Drive car, ride bike ✓\n• Arrive AT, check in, board ✓\n• Natural collocations! ✓\n\nAdvanced topics mastered! 💪"
        }
      ]
    },

    // ========================================================================
    // SET 5: COMPLETE MODULE MASTERY - FINAL INTEGRATION
    // ========================================================================
    {
      id: "m7-review-s5",
      title: "⭐ Review Set 5: Complete Module 7 Mastery 🏆",
      context: `**FINAL MASTERY CHECK**
All 12 units integrated - Ultimate challenge!`,
      category: "review",
      isReview: true,
      
      questions: [
        {
          type: "mc",
          q: "'I ___ wake up at 7 AM' (100% frequency)",
          options: [
            "always",
            "sometimes",
            "never"
          ],
          a: "always",
          successtip: "✓ ALWAYS!\nUnit 1: Frequency! Always = 100%! ⏰"
        },
        
        {
          type: "input",
          q: "'I ___ visited Paris.' (experience, present perfect)",
          a: "have",
          accept: { ignoreCase: true },
          successtip: "✓ PRESENT PERFECT!\nUnit 3: Have + past participle = experience! ✅"
        },
        
        {
          type: "mc",
          q: "Cooking method in oven:",
          options: [
            "bake",
            "fry",
            "boil"
          ],
          a: "bake",
          successtip: "✓ BAKE!\nUnit 4: Bake = in oven! 🍰"
        },
        
        {
          type: "order",
          q: "Sport verb: '___ tennis' (with ball)",
          a: "play",
          successtip: "✓ PLAY!\nUnit 5: Play + ball sports! 🎾"
        },
        
        {
          type: "mc",
          q: "'This costs £50' - who/what costs?",
          options: [
            "Things cost (not people)",
            "People cost",
            "Nothing costs"
          ],
          a: "Things cost (not people)",
          successtip: "✓ COST!\nUnit 6: Things COST! People SPEND/PAY! 💷"
        },
        
        {
          type: "input",
          q: "'Grow ___' = spend childhood (phrasal verb)",
          a: "up",
          accept: { ignoreCase: true },
          successtip: "✓ GROW UP!\nUnit 7: Phrasal verb! 👶➡️👨"
        },
        
        {
          type: "mc",
          q: "'London is ___ than Paris.' (size comparison)",
          options: [
            "bigger",
            "more big",
            "biggest"
          ],
          a: "bigger",
          successtip: "✓ BIGGER!\nUnit 8: Short adjective + -er! 📏"
        },
        
        {
          type: "order",
          q: "Strong interest: 'I'm really ___ music.'",
          a: "into",
          successtip: "✓ BE INTO!\nUnit 9: Strong interest phrase! 🎵"
        },
        
        {
          type: "mc",
          q: "Your sibling's child is your:",
          options: [
            "Niece/Nephew",
            "Cousin",
            "Sibling"
          ],
          a: "Niece/Nephew",
          successtip: "✓ NIECE/NEPHEW!\nUnit 10: Family relationships! 👶"
        },
        
        {
          type: "input",
          q: "'UN-' prefix: 'likely' → '___' (not likely)",
          a: "unlikely",
          accept: { ignoreCase: true },
          successtip: "✓ UNLIKELY!\nUnit 11: UN- = not! 🚫"
        },
        
        {
          type: "mc",
          q: "Travel collocation: '___ a car' (NOT conduct!)",
          options: [
            "drive",
            "ride",
            "conduct"
          ],
          a: "drive",
          successtip: "✓ DRIVE!\nUnit 12: Critical collocation! 🚗"
        },
        
        {
          type: "mc",
          q: "FINAL MASTERY: Module 7 includes:",
          options: [
            "ALL 12 units: Routines, Hobbies, Perfect tenses, Food, Sports, Money, Phrasal verbs, Comparing, Preferences, Family, Education, Travel!",
            "Only 6 units",
            "Only grammar"
          ],
          a: "ALL 12 units: Routines, Hobbies, Perfect tenses, Food, Sports, Money, Phrasal verbs, Comparing, Preferences, Family, Education, Travel!",
          successtip: "🎉🏆 MÓDULO 7 COMPLETELY MASTERED! 🏆🎉\n\n✅ ALL 12 UNITS REVIEWED!\n\n1️⃣ ROUTINES & TIME EXPRESSIONS\n• Frequency adverbs (always, never) ✅\n• Time prepositions (at, on, in) ✅\n• Daily routines vocabulary ✅\n\n2️⃣ HOBBIES & FREE TIME\n• Go + -ing (cycling, swimming) ✅\n• Play + sport (football, tennis) ✅\n• Do + activity (yoga, karate) ✅\n\n3️⃣ PRESENT PERFECT vs PAST SIMPLE\n• Present perfect = experience ✅\n• Past simple = specific time ✅\n• Have/Has + past participle ✅\n• For/Since duration ✅\n\n4️⃣ FOOD & COOKING\n• Food categories ✅\n• Cooking methods (fry, bake, boil) ✅\n• Kitchen vocabulary ✅\n\n5️⃣ SPORTS & FITNESS\n• Play/Do/Go patterns ✅\n• Exercise vocabulary ✅\n• Gym & facilities ✅\n\n6️⃣ SHOPPING & MONEY\n• Cost vs Spend vs Pay ✅\n• Payment methods ✅\n• UK vs US differences ✅\n\n7️⃣ CITIES & PHRASAL VERBS\n• Grow up, find out, give up ✅\n• Look after, run into ✅\n• City vocabulary ✅\n\n8️⃣ COMPARING PLACES\n• -er comparatives (bigger) ✅\n• More + adjective (more expensive) ✅\n• Irregular (better, worse) ✅\n• Than structure ✅\n\n9️⃣ LIKES & DISLIKES (B1→B2)\n• Love → Hate scale ✅\n• Be into, fond of, keen on ✅\n• Prefer, would rather ✅\n\n🔟 FAMILY & EARLY YEARS\n• Extended family ✅\n• In-laws, step-family ✅\n• Life stages ✅\n• Did for emphasis ✅\n\n1️⃣1️⃣ EDUCATION & WORD FORMATION\n• 17 academic subjects ✅\n• UN- (not), RE- (again) ✅\n• -FUL (full of), -LESS (without) ✅\n• School vocabulary ✅\n\n1️⃣2️⃣ TRAVEL & TRANSPORTATION\n• 13 vehicles ✅\n• Drive car, ride bike ✅\n• Arrive AT, check in, board ✅\n• Natural travel collocations ✅\n\n📊 MODULE 7 STATISTICS:\n• 12 Units completed ✅\n• 120 regular questions per unit ✅\n• 10 reading questions per unit ✅\n• 10 review questions per unit ✅\n• 60 final reading questions ✅\n• 60 final review questions ✅\n\n🌟 TOTAL PRACTICE: 1,680+ questions!\n\n💪 YOU CAN NOW:\n• Express routines naturally ✅\n• Talk about hobbies fluently ✅\n• Use perfect tenses correctly ✅\n• Discuss food & cooking ✅\n• Describe sports & fitness ✅\n• Handle money transactions ✅\n• Use phrasal verbs naturally ✅\n• Compare places accurately ✅\n• Express preferences sophisticatedly ✅\n• Describe family relationships ✅\n• Discuss education topics ✅\n• Travel with confidence ✅\n\n🎯 LEVEL ACHIEVED:\n✅ Solid B1 Foundation\n✅ Strong B2 Capabilities\n✅ Natural English Collocations\n✅ Complex Grammar Mastery\n✅ Rich Vocabulary (1000+ words)\n✅ Cultural Awareness (UK/US)\n\n🚀 READY FOR MODULE 8!\n\n¡FELICITACIONES! Has dominado completamente el Módulo 7 - Upper Intermediate English! 🎉✨\n\nYour English is now at a professional level! 💼🌍"
        }
      ]
    }
  ]
};
